/**
 * Spot and point light shadows on the scan (the splat shader's side of them).
 *
 * The engine renders every spot / point shadow map into one shared depth texture, the clustered
 * lighting shadow atlas: each casting light gets a tile, a spot light one perspective map in it,
 * a point light its six cube faces in a 3 x 2 block, holding distance / range. Models read the
 * atlas through the engine's clustered lighting. The splats read it here:
 *
 * - `configureLocalAtlas` sizes the atlas from the lights' resolutions (per map, per face for a
 *   point light), picks the filter (PCF3 / PCF5, it is shared) from their softness, and the
 *   border around point-light faces from the softest point light, so filter taps stay on data.
 * - `LocalShadowReceivers` keeps a copy of the atlas for the splats, made after the engine
 *   renders it (WebGL2 right after the frame, WebGPU at the start of the next update), together
 *   with where each light's map sits in it and how it was rendered (tile, matrix, position,
 *   range, cone; read from the light's shadow cameras after the render).
 *   The splats then never pair a map with another render's placement: the atlas is redrawn and
 *   re-split at times the viewer doesn't control (a light entering the view, another light
 *   starting to cast, a resize), and WebGPU colours the splats in a compute pass that isn't
 *   ordered against the shadow pass. The cost: splats follow a moving light's shadow one frame
 *   after the models, and the copy's memory (the atlas size, e.g. 16 MB at 2048).
 * - `installOmniCasterChunk` lets WebGL2's splat shader write point-light depth (see
 *   SPLAT_CASTER_LATE_GLSL in gsplat-mirror-clip.ts).
 */
import * as pc from 'playcanvas';
import {
    MAX_SPLAT_LOCAL_SHADOW_LIGHTS,
    atlasResolutionFor,
    softnessToKernelTexels,
    type ShadowTier,
} from './shadowSettings';
import { getShadowMapPlaceholder } from './shadowPlaceholder';
import { SPLAT_SHADOW_UNIFORMS } from './splatShadowShader';
import { SPLAT_CASTER_LATE_GLSL } from '../gsplat-mirror-clip';
import { shadowStrengthForLight } from './shadowChange';

/** A spot / point light the splats take shadows from (one shader slot). */
export interface LocalReceiverSlot {
    light: pc.Light;
    softness: number;
    /** World units, along the light direction. */
    bias: number;
    /** The splats cast into this light's map (their own neighbours are in it: larger size bias). */
    splatsCast: boolean;
    /** The light's current intensity, and as authored (relighting off: the shadow fades with it). */
    intensity: number;
    authoredIntensity?: number;
}

interface LocalLightInternals {
    _type: number;
    castShadows: boolean;
    visibleThisFrame: boolean;
    atlasViewportAllocated: boolean;
    atlasViewport: pc.Vec4;
    shadowUpdateMode: number;
    getRenderData(camera: pc.Camera | null, face: number): {
        shadowCamera: pc.Camera & { _node: pc.GraphNode; fov: number; farClip: number };
        shadowMatrix: pc.Mat4;
        shadowViewport: pc.Vec4;
    };
}

interface LightTextureAtlasInternals {
    shadowAtlas?: { texture?: pc.Texture; renderTargets?: pc.RenderTarget[] } | null;
    shadowAtlasResolution: number;
    shadowEdgePixels: number;
}

interface LightingParamsLike {
    shadowAtlasResolution: number;
    shadowType: number;
    shadowsEnabled: boolean;
}

/** One light's map in the atlas copy, as rendered. */
interface CapturedMap {
    kind: 1 | 2;
    pos: [number, number, number];
    range: number;
    /** Spot: the tile the map was drawn in (x, y, w, h). Point: (x, y, face size, 0). */
    rect: [number, number, number, number];
    /** Spot: direction it shines and cos(half the cone). */
    spot: [number, number, number, number];
    tanHalf: number;
    matrix: Float32Array | null;
}

/** Light-space bias per unit of splat size (as for directional lights, see shadowSystem.ts). */
const SPLAT_SIZE_BIAS_CASTING = 2.5;
const SPLAT_SIZE_BIAS_MESHES_ONLY = 0.5;
/** Slope-scaled receiver bias, in map texels. */
const SLOPE_BIAS_TEXELS = 3;
/** Fraction of a splat's size its footprint adds to the filter radius. */
const SPLAT_FOOTPRINT_FILTER = 1.5;
/** A spot light's map is drawn 4 texels inside its tile (the engine's scissor inset): taps stay within. */
const SPOT_MAX_FILTER_TEXELS = 4;
/** Border around each point-light face (engine default 3) and the most it grows to for soft lights. */
const MIN_EDGE_PIXELS = 3;
const MAX_EDGE_PIXELS = 8;

const _fwd = new pc.Vec3();

export const LOCAL_SHADOW_UNIFORMS = {
    pos: 'uSplatLocalPos[0]',
    params: 'uSplatLocalParams[0]',
    rect: 'uSplatLocalRect[0]',
    spot: 'uSplatLocalSpot[0]',
    texel: 'uSplatLocalTexel[0]',
    matrix: 'uSplatLocalMatrix[0]',
    atlasParams: 'uSplatLocalAtlas',
    atlas: SPLAT_SHADOW_UNIFORMS.atlas,
} as const;

function atlasOf(app: pc.AppBase): LightTextureAtlasInternals | null {
    return (app.renderer as unknown as { lightTextureAtlas?: LightTextureAtlasInternals } | undefined)?.lightTextureAtlas ?? null;
}

/**
 * Size the shared spot / point shadow atlas and pick its filter and point-light border for the
 * casting spot / point lights. Returns true when something changed that needs the maps redrawn
 * (the engine redraws them itself after a resize; the border changes the face cameras).
 */
export function configureLocalAtlas(
    app: pc.AppBase,
    lights: ReadonlyArray<{ type: 'spot' | 'point'; resolution: number; softness: number }>,
    tier: Pick<ShadowTier, 'atlasMaxResolution'>,
): boolean {
    if (lights.length === 0) return false;
    const lighting = (app.scene as unknown as { lighting?: LightingParamsLike }).lighting;
    if (!lighting) return false;
    let changed = false;
    const size = atlasResolutionFor(lights, tier);
    if (size > 0 && lighting.shadowAtlasResolution !== size) {
        lighting.shadowAtlasResolution = size;
    }
    // The filter is one setting for every spot / point light (clustered lighting): PCF5 once one
    // of them is soft, as directional lights pick per light.
    const soft = lights.some((l) => l.softness >= 0.5);
    const shadowType = soft ? pc.SHADOW_PCF5_32F : pc.SHADOW_PCF3_32F;
    if (lighting.shadowType !== shadowType) lighting.shadowType = shadowType;
    // Filter taps on a point-light face may reach this far past its edge (the engine draws each
    // face that much wider), so the splats' softness doesn't read the neighbouring face.
    const atlas = atlasOf(app);
    if (atlas) {
        let kernel = 0;
        for (const l of lights) if (l.type === 'point') kernel = Math.max(kernel, softnessToKernelTexels(l.softness));
        const edge = Math.min(MAX_EDGE_PIXELS, Math.max(MIN_EDGE_PIXELS, Math.ceil(kernel) + 1));
        if (atlas.shadowEdgePixels !== edge) {
            atlas.shadowEdgePixels = edge;
            changed = true;
        }
    }
    return changed;
}

/** Current point-light face border (for change keys). */
export function localAtlasEdgePixels(app: pc.AppBase): number {
    return atlasOf(app)?.shadowEdgePixels ?? MIN_EDGE_PIXELS;
}

/**
 * WebGL2: the point-light caster helper needs the engine's matrices, which GLSL declares after
 * the modify chunk; append it to the engine's gsplatOutputVS on the splat materials (the quad
 * renderer copies their chunks and defines). WGSL needs nothing.
 */
export function installOmniCasterChunk(device: pc.GraphicsDevice, materials: ReadonlyArray<pc.Material>, installed: WeakSet<pc.Material>): void {
    if (device.isWebGPU) return;
    const engineChunks = (pc as unknown as { ShaderChunks?: { get(device: pc.GraphicsDevice, lang: string): { get(name: string): string | undefined } } }).ShaderChunks;
    const engineOutput = engineChunks?.get(device, pc.SHADERLANGUAGE_GLSL).get('gsplatOutputVS');
    if (!engineOutput) return;
    const source = engineOutput + SPLAT_CASTER_LATE_GLSL;
    for (const material of materials) {
        if (installed.has(material)) continue;
        installed.add(material);
        material.getShaderChunks(pc.SHADERLANGUAGE_GLSL).set('gsplatOutputVS', source);
        (material as pc.ShaderMaterial).setDefine('RL_OMNI_CASTER', true);
        material.update();
    }
}

/**
 * The splats' copy of the spot / point shadow atlas and the uniforms that go with it. See the
 * file comment.
 */
export class LocalShadowReceivers {
    private readonly app: pc.AppBase;
    private copy: { texture: pc.Texture; target: pc.RenderTarget } | null = null;
    private readonly retired: Array<{ texture: pc.Texture; target: pc.RenderTarget }> = [];
    /** Maps in the copy (lights whose tile was valid when it was taken). */
    private captured = new Map<pc.Light, CapturedMap>();
    private capturedAtlasSize = 0;
    private capturedEdge = MIN_EDGE_PIXELS;
    /** A placement recorded after a render whose atlas is still to be copied (copyPending). */
    private pending: { maps: Map<pc.Light, CapturedMap>; atlasSize: number; edge: number } | null = null;
    /** Lights the engine renders this frame (seen at cull:end, before it consumes THISFRAME). */
    private readonly renderingNow = new Set<pc.Light>();
    private slots: Array<LocalReceiverSlot | null> = new Array(MAX_SPLAT_LOCAL_SHADOW_LIGHTS).fill(null);
    /** Casting spot / point lights (also those the splats don't sample: their render moves the atlas too). */
    private readonly casting = new Set<pc.Light>();
    /** Atlas copies made (diagnostics). */
    copies = 0;
    /** Changes whenever the copy or what it holds changes (for the binding key). */
    private version = 0;

    readonly pos = new Float32Array(4 * MAX_SPLAT_LOCAL_SHADOW_LIGHTS);
    readonly params = new Float32Array(4 * MAX_SPLAT_LOCAL_SHADOW_LIGHTS);
    readonly rect = new Float32Array(4 * MAX_SPLAT_LOCAL_SHADOW_LIGHTS);
    readonly spot = new Float32Array(4 * MAX_SPLAT_LOCAL_SHADOW_LIGHTS);
    readonly texel = new Float32Array(4 * MAX_SPLAT_LOCAL_SHADOW_LIGHTS);
    readonly matrix = new Float32Array(16 * MAX_SPLAT_LOCAL_SHADOW_LIGHTS);
    readonly atlasParams = new Float32Array(4);

    private readonly onCullEnd = () => this.recordRendering();

    constructor(app: pc.AppBase) {
        this.app = app;
        app.scene.on('cull:end', this.onCullEnd);
    }

    /** This frame's receiving slots (in shader slot order) and every casting spot / point light. */
    setLights(slots: ReadonlyArray<LocalReceiverSlot>, casting: Iterable<pc.Light>): void {
        for (let i = 0; i < MAX_SPLAT_LOCAL_SHADOW_LIGHTS; i++) this.slots[i] = slots[i] ?? null;
        this.casting.clear();
        for (const light of casting) this.casting.add(light);
    }

    /** True when a slot's light has a map in the copy (the splats show its shadow). */
    has(light: pc.Light): boolean {
        return this.captured.has(light);
    }

    /** Lights the splats sample that have no map in the copy yet: they need a render. */
    missing(): pc.Light[] {
        const out: pc.Light[] = [];
        for (const s of this.slots) if (s && !this.captured.has(s.light)) out.push(s.light);
        return out;
    }

    private recordRendering(): void {
        this.renderingNow.clear();
        const lighting = (this.app.scene as unknown as { lighting?: LightingParamsLike }).lighting;
        if (lighting && !lighting.shadowsEnabled) return;
        for (const light of this.casting) {
            const l = light as unknown as LocalLightInternals;
            if (l.castShadows && l.visibleThisFrame && l.atlasViewportAllocated && l.shadowUpdateMode !== pc.SHADOWUPDATE_NONE) {
                this.renderingNow.add(light);
            }
        }
    }

    /**
     * After the frame rendered: when the engine drew any spot / point map (or the splats need a
     * map the copy lacks), record where every light's map is and copy the atlas. WebGL2 copies
     * now (a blit, done in order). WebGPU copies at the start of the next update (copyPending):
     * a copy encoded right after the render could run on the GPU before the shadow pass wrote
     * the atlas, pairing an old map with the new placement (as for directional maps, see
     * copyForReceivers in shadowSystem.ts). The copy and the placement are bound together.
     * Returns how many spot / point lights the engine rendered this frame.
     */
    afterRender(): number {
        const renders = this.renderingNow.size;
        const rendered = renders > 0;
        this.renderingNow.clear();
        if (!this.slots.some(Boolean)) {
            // nothing samples the spot / point maps: let the copy go
            this.pending = null;
            if (this.copy || this.captured.size) {
                this.captured.clear();
                if (this.copy) this.retired.push(this.copy);
                this.copy = null;
                this.version++;
            }
            return renders;
        }
        if (!rendered && this.copy && !this.pending) return renders;
        const atlas = atlasOf(this.app);
        const src = atlas?.shadowAtlas?.renderTargets?.[0]?.depthBuffer;
        if (!atlas || !src) return renders;
        this.pending = { maps: this.capture(), atlasSize: src.width, edge: atlas.shadowEdgePixels };
        if (!this.app.graphicsDevice.isWebGPU) this.copyPending();
        return renders;
    }

    /** Copy the atlas for the placement recorded by afterRender (WebGPU: called at the start of the next update). */
    copyPending(): void {
        const pending = this.pending;
        if (!pending) return;
        this.pending = null;
        const atlas = atlasOf(this.app);
        const source = atlas?.shadowAtlas?.renderTargets?.[0];
        const src = source?.depthBuffer;
        if (!atlas || !source || !src || src.width !== pending.atlasSize) return;
        const device = this.app.graphicsDevice;
        let copy = this.copy;
        if (!copy || copy.texture.width !== src.width || copy.texture.height !== src.height || copy.texture.format !== src.format) {
            const texture = new pc.Texture(device, {
                name: 'SplatLocalShadowAtlasCopy',
                format: src.format,
                width: src.width,
                height: src.height,
                mipmaps: false,
                minFilter: pc.FILTER_LINEAR,
                magFilter: pc.FILTER_LINEAR,
                addressU: pc.ADDRESS_CLAMP_TO_EDGE,
                addressV: pc.ADDRESS_CLAMP_TO_EDGE,
            });
            texture.compareOnRead = true;
            texture.compareFunc = pc.FUNC_LESS;
            // WebGL2: allocate it now (left pending, its first bind would re-upload empty storage
            // over the depth copied in; see copyForReceivers in shadowSystem.ts).
            (texture.impl as { uploadImmediate?: (device: pc.GraphicsDevice, texture: pc.Texture) => void }).uploadImmediate?.(device, texture);
            const target = new pc.RenderTarget({ name: 'SplatLocalShadowAtlasCopy', depthBuffer: texture, origin: pc.RENDERTARGET_ORIGIN_NATIVE });
            if (copy) this.retired.push(copy);
            copy = { texture, target };
            this.copy = copy;
        }
        // WebGL2 copies with a blit, which the scissor test clips: open it to the whole map (the
        // last pass left it at the canvas size, and only that corner of the atlas was copied).
        if (!device.isWebGPU) {
            const gl = device as pc.GraphicsDevice & { setViewport(x: number, y: number, w: number, h: number): void; setScissor(x: number, y: number, w: number, h: number): void };
            gl.setViewport(0, 0, src.width, src.height);
            gl.setScissor(0, 0, src.width, src.height);
        }
        const copied = (device as pc.GraphicsDevice & { copyRenderTarget(src: pc.RenderTarget, dst: pc.RenderTarget, color: boolean, depth: boolean): boolean })
            .copyRenderTarget(source, copy.target, false, true);
        this.version++;
        if (!copied) {
            this.captured.clear();
            return;
        }
        this.copies++;
        this.captured = pending.maps;
        this.capturedAtlasSize = pending.atlasSize;
        this.capturedEdge = pending.edge;
    }

    /** Where each casting light's map sits in the atlas just rendered, as the engine rendered it. */
    private capture(): Map<pc.Light, CapturedMap> {
        const next = new Map<pc.Light, CapturedMap>();
        for (const light of this.casting) {
            const l = light as unknown as LocalLightInternals;
            if (!l.castShadows || !l.atlasViewportAllocated) continue;
            const rd = l.getRenderData(null, 0);
            const cam = rd.shadowCamera;
            const node = cam._node;
            const p = node.getPosition();
            const range = Math.max(cam.farClip, 1e-3);
            if (l._type === pc.LIGHTTYPE_SPOT) {
                const v = rd.shadowViewport;
                // the shadow camera looks down its -Z (the light's -Y)
                _fwd.copy(node.forward);
                const half = (cam.fov * Math.PI) / 360;
                next.set(light, {
                    kind: 1,
                    pos: [p.x, p.y, p.z],
                    range,
                    rect: [v.x, v.y, v.z, v.w],
                    spot: [_fwd.x, _fwd.y, _fwd.z, Math.cos(half)],
                    tanHalf: Math.tan(half),
                    matrix: new Float32Array(rd.shadowMatrix.data),
                });
            } else {
                const v = l.atlasViewport;
                next.set(light, {
                    kind: 2,
                    pos: [p.x, p.y, p.z],
                    range,
                    rect: [v.x, v.y, v.z / 3, 0],
                    spot: [0, 0, 0, 0],
                    tanHalf: 1,
                    matrix: null,
                });
            }
        }
        return next;
    }

    /**
     * Fill the uniform arrays for the current slots from the copy, with this frame's strength,
     * softness and bias. Returns the texture to bind (the copy, or the placeholder) and a key of
     * everything that changes the uniforms.
     */
    fill(strength: number, relightingEnabled: boolean): { texture: pc.Texture; key: string } {
        const size = this.capturedAtlasSize;
        let active = 0;
        for (let slot = 0; slot < MAX_SPLAT_LOCAL_SHADOW_LIGHTS; slot++) {
            const s = this.slots[slot];
            const o4 = slot * 4;
            const c = s ? this.captured.get(s.light) : undefined;
            if (!s || !c || !this.copy || size <= 0) {
                this.pos.fill(0, o4, o4 + 4);
                this.params.fill(0, o4, o4 + 4);
                this.rect.fill(0, o4, o4 + 4);
                this.spot.fill(0, o4, o4 + 4);
                this.texel.fill(0, o4, o4 + 4);
                this.matrix.fill(0, slot * 16, slot * 16 + 16);
                continue;
            }
            active++;
            this.pos.set([c.pos[0], c.pos[1], c.pos[2], 1 / c.range], o4);
            // relighting off: the shadow fades with the light's intensity (animated or set below
            // what was authored); relighting on already scales the shadowed light by it
            const slotStrength = relightingEnabled ? strength : shadowStrengthForLight(strength, s.intensity, s.authoredIntensity);
            this.params.set([slotStrength, c.kind, s.bias, s.splatsCast ? SPLAT_SIZE_BIAS_CASTING : SPLAT_SIZE_BIAS_MESHES_ONLY], o4);
            this.rect.set(c.rect, o4);
            this.spot.set(c.spot, o4);
            const kernel = softnessToKernelTexels(s.softness) / size;
            if (c.kind === 1) {
                const tileTexels = Math.max(1, c.rect[2] * size);
                this.texel.set([(2 * c.tanHalf) / tileTexels, kernel, SPOT_MAX_FILTER_TEXELS / size, (c.rect[2] / (2 * c.tanHalf)) * SPLAT_FOOTPRINT_FILTER], o4);
                this.matrix.set(c.matrix!, slot * 16);
            } else {
                const faceTexels = Math.max(1, c.rect[2] * size);
                this.texel.set([2 / faceTexels, kernel, Math.max(0, this.capturedEdge - 1) / size, (c.rect[2] / 2) * SPLAT_FOOTPRINT_FILTER], o4);
                this.matrix.fill(0, slot * 16, slot * 16 + 16);
            }
        }
        this.atlasParams[0] = size;
        this.atlasParams[1] = this.capturedEdge;
        this.atlasParams[2] = SLOPE_BIAS_TEXELS;
        this.atlasParams[3] = active;
        const texture = active > 0 && this.copy ? this.copy.texture : getShadowMapPlaceholder(this.app.graphicsDevice);
        const key = `L${active > 0 ? this.version : 0}|${Array.from(this.pos).join(',')}|${Array.from(this.params).join(',')}|${Array.from(this.rect).join(',')}|${Array.from(this.texel).join(',')}|${Array.from(this.atlasParams).join(',')}`;
        return { texture, key };
    }

    /** Set the spot / point uniforms and texture on a material (after fill). */
    apply(material: pc.Material, texture: pc.Texture): void {
        material.setParameter(LOCAL_SHADOW_UNIFORMS.pos, new Float32Array(this.pos));
        material.setParameter(LOCAL_SHADOW_UNIFORMS.params, new Float32Array(this.params));
        material.setParameter(LOCAL_SHADOW_UNIFORMS.rect, new Float32Array(this.rect));
        material.setParameter(LOCAL_SHADOW_UNIFORMS.spot, new Float32Array(this.spot));
        material.setParameter(LOCAL_SHADOW_UNIFORMS.texel, new Float32Array(this.texel));
        material.setParameter(LOCAL_SHADOW_UNIFORMS.matrix, new Float32Array(this.matrix));
        material.setParameter(LOCAL_SHADOW_UNIFORMS.atlasParams, new Float32Array(this.atlasParams));
        material.setParameter(LOCAL_SHADOW_UNIFORMS.atlas, texture);
    }

    /** Clear a material's spot / point uniforms (it no longer receives). */
    static clear(material: pc.Material, device: pc.GraphicsDevice): void {
        material.setParameter(LOCAL_SHADOW_UNIFORMS.pos, new Float32Array(4 * MAX_SPLAT_LOCAL_SHADOW_LIGHTS));
        material.setParameter(LOCAL_SHADOW_UNIFORMS.atlasParams, new Float32Array(4));
        material.setParameter(LOCAL_SHADOW_UNIFORMS.atlas, getShadowMapPlaceholder(device));
    }

    /** Free copies replaced since the last call (after the materials were pointed at the new one). */
    freeRetired(): void {
        for (const old of this.retired.splice(0)) {
            old.target.destroy();
            old.texture.destroy();
        }
    }

    destroy(): void {
        this.app.scene.off('cull:end', this.onCullEnd);
        if (this.copy) this.retired.push(this.copy);
        this.copy = null;
        this.freeRetired();
        this.captured.clear();
    }
}
